import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { 
  Upload, 
  File, 
  CheckCircle, 
  X, 
  Download, 
  ChevronLeft,
  AlertCircle,
  AlertTriangle,
  Loader2,
  FileText,
  PackageCheck
} from 'lucide-react';
import { DownloadExcelTemplate } from './DownloadExcelTemplate';

function Masivo({ userId, setActiveComponent }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  function onDrop(acceptedFiles) {
    setFile(acceptedFiles[0]);
    setResult(null);
    setError(null);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  function clearFile() {
    setFile(null);
    setResult(null);
    setError(null);
  }

  async function handleUpload() {
    if (!file) {
      setError('Por favor selecciona un archivo primero.');
      return;
    }
    setUploading(true);
    setResult(null);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delivery_points/bulk_upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setResult(response.data);
      setIsSuccessModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al subir el archivo.');
    } finally {
      setUploading(false);
    }
  }

  function renderDetailsList(items, fields) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <div className="mt-3 space-y-2">
        {items.map((item, index) => (
          <div 
            key={index} 
            className="bg-white rounded-lg p-3 border border-slate-200"
          >
            <p className="text-sm font-medium text-slate-900 font-montserrat">
              {fields.map(k => item[k]).filter(Boolean).join(' • ')}
            </p>
            {(item.reason || item.error) && (
              <p className="text-xs text-slate-600 mt-1 font-montserrat line-clamp-3">
                {(item.reason || item.error).toString().slice(0, 300)}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setActiveComponent('Analytics')}
            className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base">Volver</span>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#B1336E] font-montserrat">
              Carga Masiva
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600 font-montserrat">
              Sube un archivo Excel con múltiples entregas
            </p>
          </div>

          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 border-l-4 border-[#B1336E] rounded-xl p-4 sm:p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-[#B1336E] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-bold text-[#B1336E] font-montserrat mb-2">
                  Instrucciones
                </h2>
                <div className="text-sm text-slate-700 font-montserrat space-y-1">
                  <p>• <strong>Campos obligatorios:</strong> Nombre, dirección, WhatsApp, distrito y fecha</p>
                  <p>• <strong>Formato de fecha:</strong> DD/MM/AAAA</p>
                  <p>• <strong>WhatsApp:</strong> 9 dígitos sin código de país</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`
              relative bg-white border-2 border-dashed rounded-2xl p-8 sm:p-12 
              text-center cursor-pointer transition-all duration-200 mb-6
              ${isDragActive 
                ? 'border-[#B1336E] bg-[#B1336E]/5 scale-[1.02]' 
                : 'border-slate-300 hover:border-[#B1336E] hover:bg-slate-50'
              }
            `}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-slate-900 font-montserrat mb-1">
                  {isDragActive 
                    ? '¡Suelta el archivo aquí!' 
                    : 'Arrastra un archivo o haz clic para seleccionar'
                  }
                </p>
                <p className="text-sm text-slate-600 font-montserrat">
                  Archivos Excel (.xlsx, .xls)
                </p>
              </div>
            </div>
          </div>

          {/* File Preview */}
          {file && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-[#67A43C]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#67A43C]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate font-montserrat">
                      {file.name}
                    </p>
                    <p className="text-xs text-slate-600 font-montserrat">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearFile}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200 flex-shrink-0"
                  aria-label="Quitar archivo"
                >
                  <X className="w-5 h-5 text-red-600" />
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <button
              onClick={DownloadExcelTemplate}
              className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm font-montserrat"
            >
              <Download className="w-5 h-5" />
              <span>Descargar plantilla</span>
            </button>

            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex items-center justify-center gap-2 bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Subiendo...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Subir entregas</span>
                </>
              )}
            </button>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-red-900 font-montserrat mb-1">
                    Error
                  </h3>
                  <p className="text-sm text-red-700 font-montserrat">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className="bg-[#67A43C]/10 border-l-4 border-[#67A43C] rounded-xl p-4 sm:p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-[#67A43C] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-bold text-[#67A43C] font-montserrat mb-3">
                    {result.message || 'Proceso completado'}
                  </h3>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Creadas</p>
                      <p className="text-2xl font-bold text-[#67A43C] font-montserrat">{result.created}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Enviados</p>
                      <p className="text-2xl font-bold text-[#67A43C] font-montserrat">{result.send_ok}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Fallidos</p>
                      <p className="text-2xl font-bold text-red-600 font-montserrat">{result.send_failed_count}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Omitidos</p>
                      <p className="text-2xl font-bold text-slate-600 font-montserrat">{result.skipped_count}</p>
                    </div>
                  </div>

                  {/* Failed Messages */}
                  {Array.isArray(result.send_failed) && result.send_failed.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <h4 className="text-sm font-bold text-slate-900 font-montserrat">
                          Mensajes fallidos ({result.send_failed.length})
                        </h4>
                      </div>
                      {renderDetailsList(result.send_failed, ['row', 'phone', 'status'])}
                    </div>
                  )}

                  {/* Skipped Rows */}
                  {Array.isArray(result.skipped_rows) && result.skipped_rows.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-slate-600" />
                        <h4 className="text-sm font-bold text-slate-900 font-montserrat">
                          Filas omitidas ({result.skipped_rows.length})
                        </h4>
                      </div>
                      {renderDetailsList(result.skipped_rows, ['row'])}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <PackageCheck className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-600 font-montserrat">
                  ¡Carga Completada!
                </h3>
              </div>
              
              <p className="text-sm text-slate-600 mb-6 font-montserrat">
                Las entregas han sido procesadas exitosamente. ¿Qué deseas hacer ahora?
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setActiveComponent('Analytics')}
                  className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all bg-[#B1336E] hover:bg-[#B1336E]/90 font-montserrat"
                >
                  Mis Entregas
                </button>
                <button
                  onClick={() => setActiveComponent('ControlTower')}
                  className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors font-montserrat"
                >
                  Torre de Control
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Masivo;